/**
 * La firma de Aziru en el marco de la aplicacion.
 *
 * QUE ES
 * Un boton discreto —el glifo fiel de Aziru, y nada mas— que abre su ficha de
 * presentacion: quien hizo este sistema, su numero y un boton para escribirle.
 * Es la placa del autor de una obra, no un anuncio: se ve si se busca, y no le
 * quita un pixel ni un toque a nadie que este cobrando.
 *
 * SOLO EL GLIFO, EN LOS DOS SITIOS DONDE VIVE
 * Hubo una version con la palabra escrita —"Hecho por Aziru", un renglon propio
 * en el pie de la barra lateral— y el dueño la quito: en ese pie dejaba tres
 * bloques apilados hablando de tres asuntos distintos, y una firma no es
 * informacion de la tienda. Hoy es el mismo boton en los dos lados:
 *
 *   - Escritorio y all-in-one: al filo DERECHO del renglon del estado del canal,
 *     con todo el hueco de ese renglon separandolo de "En vivo". Una placa se
 *     pone en una esquina, no en la lista.
 *   - Telefono y tablet: en el encabezado, junto al sello de la tienda.
 *
 * En escritorio hay tiempo de pasar el raton por encima, y el titulo dice de
 * quien es; en el telefono lo dice el `aria-label`. La palabra completa vive
 * donde tiene sentido leerla: dentro de la ficha.
 *
 * LA MARCA VA SUBORDINADA AL SELLO DE LA TIENDA, SIEMPRE
 * En el telefono los dos logotipos quedan a diez pixeles uno del otro. El sello
 * de la tienda mide 32 px, tiene color y es de quien paga el sistema; el glifo
 * de Aziru mide 12 de alto, va en tinta apagada y solo se enciende al pasar por
 * encima. Es la proporcion de la firma de Kory Boutique, el sistema anterior del
 * mismo estudio. Dos marcas del mismo tamaño y del mismo peso se leen como una
 * sola cosa confusa, y la que perderia es la de la tienda.
 *
 * EL AREA QUE SE TOCA NO ES LA QUE SE PINTA
 * Lo pintado son 32 px para que no compita con el sello; lo tocable son 44, que
 * es el minimo de Apple, y se consigue con un pseudo-elemento centrado
 * (`after:`) que NO ocupa lugar en la fila. Un `touch-target` de 48 px habria
 * empujado la fila del encabezado y dejado al selector de sucursal sin ancho
 * para truncar. Los 6 px que ese pseudo-elemento se mete hacia la izquierda
 * caben en el hueco de 10 px que lo separa del sello, asi que no le roba el
 * gesto de los cinco toques que enciende el diagnostico.
 */

import { useId, useState, type PointerEvent, type ReactNode } from 'react';
import {
  motion,
  useMotionTemplate,
  useMotionValue,
  useReducedMotion,
  useSpring,
  useTransform,
} from 'framer-motion';
import { DURATION, EASE } from '../lib/motion.js';
import { AZIRU } from '../lib/aziru.js';
import { AziruMark, BAR_PATH, DOT, VIEW_BOX, WORDMARK_CLS } from './AziruMark.js';
import { WhatsAppIcon } from './WhatsAppIcon.js';
import { cx, Modal } from './ui.js';

// ---------------------------------------------------------------------------
// El boton
// ---------------------------------------------------------------------------

export function AziruSignature({ className }: { className?: string }): ReactNode {
  const [open, setOpen] = useState(false);
  const label = `Hecho por ${AZIRU.name}`;

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        aria-label={label}
        title={label}
        className={cx(
          // La guarda de ancho NO vive aqui: la pone quien la necesita, que es
          // el encabezado del telefono (`max-[359px]:hidden` en `Shell`). En la
          // barra lateral —que solo existe de 1024 px para arriba— esa regla no
          // aplicaria nunca. El porque de esa medida esta escrito donde se usa.
          'group relative grid size-8 shrink-0 place-items-center rounded-lg',
          // La placa de fondo aparece SOLO al pasar por encima. En reposo el
          // glifo va suelto sobre la barra, que es lo que lo deja leerse como
          // firma; al acercarse el raton se enciende y se vuelve un control, que
          // es lo que hace falta para que alguien se anime a tocarlo.
          'text-subtle transition-colors hover:bg-surface-2 hover:text-ink active:text-ink',
          // El area tactil de 44 px, sin costo de maquetado. Ver la nota de
          // arriba: `content-[""]` es obligatorio o el pseudo-elemento no se
          // genera y el boton se queda con sus 32 px.
          'after:absolute after:left-1/2 after:top-1/2 after:size-11 after:-translate-x-1/2 after:-translate-y-1/2 after:content-[""]',
          className,
        )}
      >
        <AziruMark className="h-3 w-auto" />
      </button>

      <AziruCardSheet open={open} onClose={() => setOpen(false)} />
    </>
  );
}

// ---------------------------------------------------------------------------
// La ficha
// ---------------------------------------------------------------------------

/**
 * LA FICHA ES LA HOJA, NO UN RECUADRO DENTRO DE LA HOJA.
 *
 * Va con `bare`: sin encabezado y sin superficie propia de la hoja. Las dos
 * versiones anteriores dibujaban una placa adentro del dialogo normal, y eso
 * daba DOS cajas oscuras redondeadas —una dentro de otra— y un titulo que
 * repetia la palabra que el logotipo dice tres centimetros mas abajo. Ese
 * anidado es la mitad de por que se veia barata.
 *
 * LA COMPOSICION ES LA DE KORY BOUTIQUE, el sistema anterior del mismo estudio,
 * y eso no es copiar: es que las dos fichas son la misma pieza de la misma
 * marca, y tienen que reconocerse. De ahi vienen las cinco decisiones que la
 * definen, y ninguna es negociable por gusto:
 *
 *   1. UNA sola columna, centrada. Nada de repartir a lo ancho: una tarjeta de
 *      presentacion se lee de arriba abajo.
 *   2. El lockup es HORIZONTAL —glifo a la izquierda, palabra a la derecha— y es
 *      lo mas grande de la ficha. Apilado, como estaba, la marca se leia como un
 *      icono con una etiqueta debajo.
 *   3. Entre la palabra y el lema va un FILETE de oro, corto y centrado. Es lo
 *      que separa la firma del texto sin meter una linea de caja.
 *   4. El numero va DEBAJO del boton, no encima: el boton es la accion y el
 *      numero es el respaldo para quien prefiera marcar.
 *   5. El rotulo dice "FUE CREADO POR". Es la formula del estudio.
 *
 * CABE ENTERA, SIEMPRE (§9). El caso apretado es el telefono ACOSTADO —844 x
 * 390, donde la hoja se topa en 358 px—: ahi la variante `short` encoge relleno,
 * marca y tipografia sin tocar la composicion. Es la regla del §9 aplicada al
 * derecho: lo que cuesta alto se pide con una consulta de ALTO, y la version
 * compacta es el caso base.
 */
function AziruCardSheet({ open, onClose }: { open: boolean; onClose: () => void }): ReactNode {
  return (
    <Modal open={open} onClose={onClose} title={AZIRU.name} size="sm" bare>
      <AziruCard3D />
    </Modal>
  );
}

// ---------------------------------------------------------------------------
// El 3D
// ---------------------------------------------------------------------------

/**
 * LA PROFUNDIDAD ES DE VERDAD, Y ES BARATA.
 *
 * No hay motor 3D ni una dependencia nueva: son transformaciones 3D del
 * navegador, que compone la GPU. Todo lo que se mueve es `transform`, `opacity`
 * o la POSICION de un degradado —nada que obligue a recalcular el maquetado—
 * asi que la ficha se anima sin quitarle un cuadro a la caja que pueda estar
 * cobrando detras.
 *
 * QUE APLANA EL 3D, Y POR QUE ESTA ESCRITO ASI
 * Un elemento con `transform-style: preserve-3d` vuelve a ser plano en cuanto
 * lleva `overflow`, `filter`, `opacity` menor que 1, una mascara o un modo de
 * fusion. Eso descarto tres ideas que parecian buenas: un desenfoque de entrada,
 * una capa iridiscente con `mix-blend-mode` —el truco de las tarjetas
 * holograficas— y recortar la ficha para el destello. Las tres habrian aplanado
 * justo el 3D que venian a adornar. Por eso el brillo se pinta como FONDO de una
 * capa redondeada, que se recorta sola con su radio, y el unico
 * `overflow-hidden` vive en una rama sin hijos en 3D.
 *
 * Y HAY UNA TRAMPA MAS, que no se ve y cuesta el efecto entero: framer compone
 * el `transform` de lo que anima y PISA el que uno escribe a mano. Una capa con
 * `style={{ transform: 'translateZ(20px)' }}` que ademas anime `x`, `y` o
 * `scale` pierde su profundidad en el primer cuadro, sin error y sin aviso. La
 * profundidad de esas capas va en `z`, que framer si compone. Se cazo midiendo
 * cuanto AGRANDA la perspectiva a cada capa: un plano a `translateZ(N)` bajo
 * perspectiva P se ve P/(P-N) veces mas grande, asi que un 1.0000 exacto delata
 * una cadena aplanada.
 *
 * LA INCLINACION NO BAILA, Y ESA FUE LA QUEJA.
 * Antes seguia al apuntador con un resorte de rebote (140/18) y 15 grados de
 * recorrido sobre TODA la ficha, boton de WhatsApp incluido: el objeto oscilaba
 * al detenerse y el blanco al que uno queria dar clic se movia bajo el dedo.
 * Ahora el resorte esta SOBREAMORTIGUADO (90/34) —llega y se detiene, sin
 * pasarse: con rebote se siente a gelatina, sin el, a cristal pesado—, el
 * recorrido baja a 6 y 8 grados, y solo la mueve un RATON
 * (`pointerType === 'mouse'`). Con el dedo, arrastrar sobre la ficha es
 * intentar desplazar la hoja, y pelearse con eso es lo que se siente incomodo.
 *
 * LA ENTRADA VA LENTA A PROPOSITO, y en un orden que se lee.
 * La anterior metia seis cosas en un segundo y se sentia atropellada. Esta se
 * toma dos segundos y medio y va en el orden en que se dibujaria la marca a
 * mano: primero el TRAZO del glifo, que se dibuja de verdad —un contorno que
 * avanza, no una barra que se destapa—, despues el relleno, despues el punto,
 * despues la palabra letra por letra, y al final el filete que se abre desde el
 * centro y el pie. Lo que hay que LEER —el boton y el numero— esta puesto antes
 * del segundo y medio; lo que llega despues es el reflejo, que es adorno.
 *
 * CON `prefers-reduced-motion` NO SE MUEVE NADA, ni siquiera al apuntar: la
 * ficha aparece ya puesta y de frente. Nunca queda a medias.
 */

/** El canto del glifo: seis copias corridas en x, y, z. */
const EDGES = [
  { z: -20, dx: 1.1, dy: 0.8, opacity: 0.09 },
  { z: -16, dx: 0.9, dy: 0.66, opacity: 0.13 },
  { z: -12, dx: 0.7, dy: 0.52, opacity: 0.18 },
  { z: -9, dx: 0.52, dy: 0.38, opacity: 0.24 },
  { z: -6, dx: 0.33, dy: 0.24, opacity: 0.3 },
  { z: -3, dx: 0.16, dy: 0.12, opacity: 0.38 },
] as const;

/** Sobreamortiguado: llega y se detiene. Ver la nota de arriba. */
const TILT_SPRING = { stiffness: 90, damping: 34, mass: 1 } as const;

/** La ficha llega desde el fondo y ASIENTA CON PESO. */
const ENTER_SPRING = { type: 'spring', stiffness: 44, damping: 20, mass: 1.4 } as const;

const TILT_X = 6;
const TILT_Y = 8;

/** El compas de la entrada. Un solo sitio, para poder leerla como partitura. */
const T = {
  trazo: 0.35,
  relleno: 1.15,
  punto: 1.25,
  palabra: 1.4,
  filete: 1.75,
  lema: 1.85,
  boton: 1.98,
  numero: 2.1,
  reflejo: 2.25,
} as const;

function AziruCard3D(): ReactNode {
  const reduced = useReducedMotion() ?? false;

  const px = useMotionValue(0);
  const py = useMotionValue(0);

  const rotateX = useSpring(useTransform(py, [-0.5, 0.5], [TILT_X, -TILT_X]), TILT_SPRING);
  const rotateY = useSpring(useTransform(px, [-0.5, 0.5], [-TILT_Y, TILT_Y]), TILT_SPRING);

  /* El brillo se mueve al reves que la ficha, como la luz sobre una superficie
     que se ladea. Es lo que la hace leerse metalica y no pintada. */
  const glareX = useSpring(useTransform(px, [-0.5, 0.5], [16, 84]), TILT_SPRING);
  const glareY = useSpring(useTransform(py, [-0.5, 0.5], [6, 94]), TILT_SPRING);
  const glare = useMotionTemplate`radial-gradient(52% 64% at ${glareX}% ${glareY}%, rgb(255 255 255 / 0.13), rgb(255 255 255 / 0.04) 40%, transparent 72%)`;

  const follow = (event: PointerEvent<HTMLDivElement>): void => {
    if (event.pointerType !== 'mouse') return;
    const box = event.currentTarget.getBoundingClientRect();
    px.set((event.clientX - box.left) / box.width - 0.5);
    py.set((event.clientY - box.top) / box.height - 0.5);
  };

  const release = (): void => {
    px.set(0);
    py.set(0);
  };

  return (
    <div
      className="relative [perspective-origin:50%_40%] [perspective:1300px]"
      onPointerMove={reduced ? undefined : follow}
      onPointerLeave={reduced ? undefined : release}
      onPointerCancel={reduced ? undefined : release}
    >
      {/* Capa 1 — LA ENTRADA: llega desde el fondo, girada, y asienta. */}
      <motion.div
        className="[transform-style:preserve-3d]"
        initial={reduced ? false : { opacity: 0, rotateX: -16, rotateY: 22, scale: 0.92, z: -180 }}
        animate={{ opacity: 1, rotateX: 0, rotateY: 0, scale: 1, z: 0 }}
        transition={
          reduced
            ? { duration: 0 }
            : { ...ENTER_SPRING, opacity: { duration: DURATION.slow, ease: EASE.out } }
        }
      >
        {/* Capa 2 — LA INCLINACION, que sigue al raton. */}
        <motion.div
          className="will-change-transform [transform-style:preserve-3d]"
          style={reduced ? undefined : { rotateX, rotateY }}
        >
          {/*
            LA FICHA. El canto de oro es un degradado conico en el BORDE, con la
            superficie recortada al relleno: un solo elemento, sin envoltorio que
            romperia la cadena del 3D. Es lo que le da filo metalico en vez de
            una linea de color.
          */}
          <div
            className={cx(
              'relative rounded-[1.75rem] border border-transparent text-center',
              'px-6 py-9 short:px-5 short:py-5 sm:px-9 sm:py-10 short:sm:px-6 short:sm:py-6',
              '[transform-style:preserve-3d]',
            )}
            style={{
              background: [
                'radial-gradient(120% 78% at 50% -10%, color-mix(in srgb, var(--color-brand-gold) 15%, transparent), transparent 56%) padding-box',
                'linear-gradient(170deg, color-mix(in srgb, var(--color-ink) 6%, var(--color-surface)), var(--color-surface) 46%, color-mix(in srgb, var(--color-bg) 70%, var(--color-surface))) padding-box',
                'conic-gradient(from 205deg at 50% 50%, color-mix(in srgb, var(--color-brand-gold) 16%, transparent), color-mix(in srgb, var(--color-brand-gold) 80%, transparent) 20%, color-mix(in srgb, var(--color-brand-gold) 14%, transparent) 42%, color-mix(in srgb, var(--color-brand-gold) 62%, transparent) 66%, color-mix(in srgb, var(--color-brand-gold) 16%, transparent)) border-box',
              ].join(', '),
              boxShadow: '0 40px 80px -40px rgb(0 0 0 / 0.9), 0 8px 24px -16px rgb(0 0 0 / 0.6)',
            }}
          >
            {/* El brillo que sigue a la luz. Se recorta solo con su radio: un
                `overflow-hidden` aqui aplanaria la ficha entera. */}
            <motion.div
              aria-hidden
              className="pointer-events-none absolute inset-0 rounded-[1.75rem]"
              style={reduced ? { background: 'transparent' } : { background: glare }}
            />

            {/* El reflejo que cruza UNA vez, al final. Va en una rama sin hijos
                en 3D, que es donde `overflow-hidden` no hace daño. */}
            {reduced ? null : (
              <div
                aria-hidden
                className="pointer-events-none absolute inset-0 overflow-hidden rounded-[1.75rem]"
              >
                <motion.div
                  className="absolute inset-y-[-60%] left-0 w-1/3 -skew-x-[14deg] bg-gradient-to-r from-transparent via-white/25 to-transparent"
                  initial={{ x: '-260%', opacity: 0 }}
                  animate={{ x: ['-260%', '420%'], opacity: [0, 1, 1, 0] }}
                  transition={{
                    duration: 1.3,
                    delay: T.reflejo,
                    times: [0, 0.12, 0.82, 1],
                    ease: EASE.inOut,
                  }}
                />
              </div>
            )}

            {/* El filo de arriba, iluminado. Un pelo de un pixel es la diferencia
                entre un recuadro dibujado y una pieza con canto. */}
            <div
              aria-hidden
              className="pointer-events-none absolute inset-x-12 top-0 h-px bg-gradient-to-r from-transparent via-white/40 to-transparent"
            />

            <Capa z={12} delay={0.2} reduced={reduced}>
              <p className="text-[11px] font-semibold uppercase leading-[1.7] tracking-[0.2em] text-brand-gold short:text-[10px]">
                Este sistema inteligente
                <br />
                fue creado por
              </p>
            </Capa>

            {/* EL LOCKUP, horizontal y lo mas grande de la ficha. */}
            <div className="mt-7 flex items-center justify-center gap-4 short:mt-4 short:gap-3 [transform-style:preserve-3d]">
              <AziruGlyph reduced={reduced} />
              <AziruWord reduced={reduced} />
            </div>

            {/* El filete: se abre desde el centro. */}
            <motion.div
              aria-hidden
              className="mx-auto mt-7 h-px w-16 origin-center bg-gradient-to-r from-transparent via-brand-gold/70 to-transparent short:mt-4"
              style={{ transform: 'translateZ(10px)' }}
              initial={reduced ? false : { scaleX: 0, opacity: 0 }}
              animate={{ scaleX: 1, opacity: 1 }}
              transition={
                reduced ? { duration: 0 } : { duration: DURATION.slow, delay: T.filete, ease: EASE.out }
              }
            />

            <Capa z={14} delay={T.lema} reduced={reduced}>
              <p className="mt-5 text-[15px] text-muted short:mt-3 short:text-[13px]">
                {AZIRU.tagline}
              </p>
            </Capa>

            {/* El boton lleva el verde de WhatsApp porque el boton ES WhatsApp;
                usar el jade del sistema lo haria pasar por una accion interna de
                la aplicacion. Es el mismo de la placa que cierra el recorrido de
                demostracion y el mismo numero que va al pie del ticket impreso.

                LO QUE CAMBIA ES EL MATERIAL, no el color: un rectangulo de color
                plano se lee como el boton de una pagina de anuncios. Con el filo
                superior iluminado y el halo verde bajo el, se lee como una tecla
                fisica, que es lo mismo que hace `BrandLogo` con el sello de la
                tienda.

                La sombra va en `style` y NO en `shadow-[...]`: son DOS sombras
                con comas adentro y Tailwind no le sacaba nada a ese valor
                arbitrario. No fallaba a la vista —el boton salia plano y parecia
                una decision de diseño—, se cazo leyendo el `boxShadow`
                calculado, que venia en `rgba(0,0,0,0)`. */}
            <Capa z={22} delay={T.boton} reduced={reduced} className="mt-6 short:mt-4">
              <a
                href={AZIRU.whatsappUrl}
                target="_blank"
                rel="noreferrer"
                className={cx(
                  'inline-flex h-12 items-center justify-center gap-2.5 rounded-full px-7',
                  'bg-[#25d366] text-[16px] font-semibold text-[#06230e]',
                  'transition-[filter,transform] duration-150',
                  'hover:brightness-[1.06] active:scale-[0.985]',
                  'short:h-10 short:px-5 short:text-[14px]',
                )}
                style={{
                  boxShadow:
                    'inset 0 1px 0 rgb(255 255 255 / 0.45), 0 10px 26px -12px rgb(37 211 102 / 0.8)',
                }}
              >
                <WhatsAppIcon className="size-5 shrink-0" />
                Escribele a {AZIRU.name}
              </a>
            </Capa>

            <Capa z={16} delay={T.numero} reduced={reduced}>
              <p className="tabular mt-4 text-[15px] font-medium text-muted short:mt-2.5 short:text-[13px]">
                {AZIRU.phoneDisplay}
              </p>
            </Capa>
          </div>
        </motion.div>
      </motion.div>
    </div>
  );
}

/**
 * Una capa de la ficha: su profundidad y su turno de entrada.
 *
 * La profundidad va en `z` y NO en un `transform` de estilo, porque aqui tambien
 * se anima `y` y framer pisa el transform escrito a mano. Ver la nota de arriba.
 */
function Capa({
  z,
  delay,
  reduced,
  className,
  children,
}: {
  z: number;
  delay: number;
  reduced: boolean;
  className?: string;
  children: ReactNode;
}): ReactNode {
  return (
    <motion.div
      className={cx('[transform-style:preserve-3d]', className)}
      initial={reduced ? false : { opacity: 0, y: 10, z }}
      animate={{ opacity: 1, y: 0, z }}
      transition={reduced ? { duration: 0 } : { duration: DURATION.slow, delay, ease: EASE.out }}
    >
      {children}
    </motion.div>
  );
}

/**
 * El glifo: se DIBUJA.
 *
 * Un contorno que avanza a lo largo del trazo —`stroke-dasharray` con el
 * desplazamiento animado— y detras, cuando el contorno llega al final, entra el
 * relleno. Es lo que hace una marca que se firma; destaparla con un recorte,
 * como estaba, es lo que hace cualquier cosa.
 *
 * El canto son seis copias del MISMO trazo corridas en x, y, z: un SVG es plano
 * por dentro y no se le puede dar grosor a un camino. Se corren tambien en x e y
 * porque en una pantalla QUIETA la profundidad sola no se ve, y sin eso la marca
 * parecia una calcomania y el relieve solo existia mientras uno movia el raton.
 */
function AziruGlyph({ reduced }: { reduced: boolean }): ReactNode {
  // Los `id` de un SVG son globales al documento: con dos fichas montadas —la de
  // la barra lateral y la del encabezado, que conviven en el arbol— la segunda
  // pisaria las definiciones de la primera.
  const uid = `az-${useId().replace(/[^a-zA-Z0-9]/g, '')}`;

  return (
    <div className="relative aspect-[37.5/27] h-12 short:h-9 [transform-style:preserve-3d]">
      {/* La sombra de contacto: es lo que APOYA el glifo sobre la ficha. */}
      <motion.div
        aria-hidden
        className="pointer-events-none absolute left-1/2 top-full h-4 w-[130%]"
        style={{
          transform: 'translateX(-50%) translateY(-40%) translateZ(-24px)',
          background: 'radial-gradient(50% 50% at 50% 50%, rgb(0 0 0 / 0.5), transparent 72%)',
        }}
        initial={reduced ? false : { opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={reduced ? { duration: 0 } : { duration: DURATION.slow, delay: T.relleno }}
      />

      {EDGES.map((edge, i) => (
        <motion.svg
          key={edge.z}
          viewBox={VIEW_BOX}
          aria-hidden
          className="absolute inset-0 size-full"
          fill="currentColor"
          style={{ transform: `translate3d(${edge.dx}%, ${edge.dy}%, ${edge.z}px)` }}
          initial={reduced ? false : { opacity: 0 }}
          animate={{ opacity: edge.opacity }}
          transition={
            reduced
              ? { duration: 0 }
              : { duration: DURATION.slow, delay: T.relleno + (EDGES.length - 1 - i) * 0.05 }
          }
        >
          <circle cx={DOT.cx} cy={DOT.cy} r={DOT.r} />
          <path d={BAR_PATH} />
        </motion.svg>
      ))}

      {/* LA CARA. El degradado no la pinta de otro color: es `currentColor`
          perdiendo fuerza hacia abajo, o sea la misma tinta con luz encima. Asi
          la marca sigue siendo blanca en tema oscuro y negra en tema claro
          —como manda el original— pero deja de verse como papel recortado. */}
      <svg viewBox={VIEW_BOX} aria-hidden className="absolute inset-0 size-full text-ink">
        <defs>
          <linearGradient id={`${uid}-face`} x1="0" y1="0" x2="0.35" y2="1">
            <stop offset="0%" stopColor="currentColor" stopOpacity="1" />
            <stop offset="58%" stopColor="currentColor" stopOpacity="0.92" />
            <stop offset="100%" stopColor="currentColor" stopOpacity="0.7" />
          </linearGradient>
        </defs>

        {/* El trazo que se dibuja. `pathLength` normaliza el camino a 1, asi que
            el guion y su desplazamiento se escriben en fracciones y no hay que
            medir el contorno a mano. */}
        <motion.path
          d={BAR_PATH}
          fill="none"
          stroke="currentColor"
          strokeWidth={0.8}
          strokeLinejoin="round"
          pathLength={1}
          strokeDasharray="1 1"
          initial={reduced ? false : { strokeDashoffset: 1, opacity: 1 }}
          animate={{ strokeDashoffset: 0, opacity: reduced ? 0 : [1, 1, 0] }}
          transition={
            reduced
              ? { duration: 0 }
              : {
                  strokeDashoffset: { duration: 0.85, delay: T.trazo, ease: EASE.inOut },
                  opacity: { duration: 0.5, delay: T.relleno, times: [0, 0.4, 1] },
                }
          }
        />

        {/* El relleno entra cuando el contorno termino. */}
        <motion.path
          d={BAR_PATH}
          fill={`url(#${uid}-face)`}
          initial={reduced ? false : { opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={reduced ? { duration: 0 } : { duration: DURATION.slow, delay: T.relleno }}
        />
      </svg>

      {/* EL PUNTO, adelantado: es el que produce el paralaje. Llega desde la
          izquierda y encaja, que es lo que hace una pieza que se coloca. */}
      <motion.svg
        viewBox={VIEW_BOX}
        aria-hidden
        className="absolute inset-0 size-full text-ink"
        initial={reduced ? false : { opacity: 0, x: -16, scale: 0.6, z: 22 }}
        animate={{ opacity: 1, x: 0, scale: 1, z: 22 }}
        style={{ transformOrigin: '17% 76%' }}
        transition={
          reduced
            ? { duration: 0 }
            : { type: 'spring', stiffness: 160, damping: 18, mass: 0.9, delay: T.punto }
        }
      >
        <circle cx={DOT.cx} cy={DOT.cy} r={DOT.r} fill={`url(#${uid}-face)`} />
      </motion.svg>
    </div>
  );
}

/**
 * La palabra, letra por letra y de gris a blanco.
 *
 * No es un fundido: cada letra llega desde abajo y ADEMAS se enciende —del gris
 * apagado a la tinta plena—, que es como se revela un rotulo grabado. El lector
 * de pantalla lee una sola palabra, no cinco letras sueltas.
 */
function AziruWord({ reduced }: { reduced: boolean }): ReactNode {
  return (
    <motion.span
      aria-label={AZIRU.name}
      className={cx('inline-flex text-[2.75rem] text-ink short:text-[2rem]', WORDMARK_CLS)}
      style={{ transform: 'translateZ(28px)' }}
      initial={reduced ? false : 'hidden'}
      animate="visible"
      variants={reduced ? undefined : WORD_PARENT}
    >
      {AZIRU.name.split('').map((letter, i) => (
        <motion.span
          key={`${letter}-${i}`}
          aria-hidden
          variants={reduced ? undefined : LETTER}
          transition={{ duration: 0.55, ease: EASE.out }}
        >
          {letter}
        </motion.span>
      ))}
    </motion.span>
  );
}

const WORD_PARENT = {
  hidden: {},
  visible: { transition: { staggerChildren: 0.07, delayChildren: T.palabra } },
} as const;

const LETTER = {
  hidden: { opacity: 0, y: 20, color: 'var(--color-subtle)' },
  visible: { opacity: 1, y: 0, color: 'var(--color-ink)' },
} as const;
